import type { Order } from './orders-client';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body } = options;
  const url = `${API_BASE_URL}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  };

  if (body && method !== 'GET') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  if (response.status === 401 || response.status === 403) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { status: response.status } }));
    throw new Error(`Unauthorized (${response.status})`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API error (${response.status}): ${errorText}`);
  }

  return response.json();
}

export interface CustomerAddress {
  id: string;
  raw: string;
  source?: string;
  customerInput?: string;
  coordinates?: { lat: number; lng: number };
  resolvedAddress?: string;
  street?: string;
  doorNumber?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  apartment?: string;
  geocodeQuality?: string;
  label?: string;
  lastUsedAt: string;
}

export interface Customer {
  id: string;
  phone: string;
  name?: string;
  addresses?: CustomerAddress[];
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

export interface CustomerProfile {
  customer: Customer;
  orders: Order[];
  pagination: PaginationMeta;
}

export const customersClient = {
  async getProfile(
    phone: string,
    params?: { page?: number; limit?: number },
  ): Promise<ApiResponse<CustomerProfile>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const query = searchParams.toString();
    return api<ApiResponse<CustomerProfile>>(
      `/customers/phone/${encodeURIComponent(phone)}/profile${query ? `?${query}` : ''}`,
    );
  },
};
